import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { suggestionsApi, GetSuggestionsParams } from '@/api/suggestions';
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination';

// Query keys
export const suggestionKeys = {
  all: ['suggestions'] as const,
  lists: () => [...suggestionKeys.all, 'list'] as const,
  list: (params?: GetSuggestionsParams) => [...suggestionKeys.lists(), params] as const,
  details: () => [...suggestionKeys.all, 'detail'] as const,
  detail: (id: number) => [...suggestionKeys.details(), id] as const,
};

// Hook to get suggestions list (single page)
export const useSuggestions = (params?: GetSuggestionsParams) => {
  return useQuery({
    queryKey: suggestionKeys.list(params),
    queryFn: () => suggestionsApi.list(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get suggestions list with infinite pagination
export const useSuggestionsInfinite = (params?: Omit<GetSuggestionsParams, 'page' | 'page_size'>) => {
  return useInfiniteQuery({
    queryKey: suggestionKeys.list(params),
    queryFn: ({ pageParam = 1 }) =>
      suggestionsApi.list({
        ...params,
        page: pageParam,
        page_size: DEFAULT_PAGE_SIZE
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !allPages) return undefined;
      if (lastPage.next) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get a specific suggestion
export const useSuggestion = (id: number) => {
  return useQuery({
    queryKey: suggestionKeys.detail(id),
    queryFn: () => suggestionsApi.get(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
